/**
 * Section-level completion + progress (s79 "star pass" → P2 #3 shared computation).
 *
 * The completion/progress LOGIC now lives in the shared `computeSectionProgress`
 * (`shared/section-progress.ts`, G7) so the server and client share one definition.
 * This file is the CLIENT ADAPTER: it maps the page's cache shape (fieldId-keyed
 * entries) into the shared function's plain `SectionProgressInput`, resolves Category
 * A membership through the fail-closed `isCategoryAField` (the SDE swap-point stays
 * client-side), and exposes:
 *   - `sectionProgress(section, entries)` → the full `{ complete, filled, total }`
 *     (or `null`) — backs the section-header fraction + dashboard progress.
 *   - `isSectionComplete(section, entries)` → a thin wrapper = `progress?.complete`.
 * Do NOT fork a parallel copy of the completion logic — extend the shared function.
 *
 * Design (unchanged, now enforced by the shared function):
 *  - Keys on `is_required` ("starred") fields; a pure seeded placeholder row never
 *    counts (⚠️-E). About You is the identity card: ≥5 of 7 keys (⚠️-F). Category A
 *    fields are presence-checked as "a non-seeded entry with a value" (⚠️-A — the
 *    Item 21 plaintext read). Hidden sections have no green-check slot (⚠️-C).
 *    `executor_identified` is not a generic-category section (⚠️-B, handled upstream).
 */
import { isCategoryAField } from "./data-classification";
import { isSeverityOptionalField } from "@shared/constants/severity-optional-fields";
import { evaluateShowIf, type ShowIfCondition, type ShowIfEntryMap } from "./evaluate-show-if";
import { sectionDeclaresEmptyStarter } from "./table-row-visibility";
import { buildSectionProgressInput, type RawProgressRow } from "@shared/section-progress-input";
import { isLockedBuiltIn, isSeededDefaultRow } from "@shared/constants/locked-rows";
import { isEssentialTiedRow } from "@shared/essentials-spec";
import { isTopZoneScaffold, topZoneCurationFieldKey, TOP_ZONE_SEEDED_STARTER_SECTIONS } from "@shared/constants/top-zone-scaffolds";
import {
  computeSectionProgress,
  type SectionProgressInput,
  type SectionProgressEntry,
  type SectionProgressResult,
} from "@shared/section-progress";

// Minimal structural shapes — the real `@shared/schema` GenericField / GenericEntry
// and the page's SectionWithFields satisfy these, and they keep the unit tests light.
export interface CompletionField {
  id: string;
  fieldKey: string;
  fieldType?: string;
  isRequired?: boolean;
  isCategoryA?: boolean;
  /** `generic_fields.conditions` (showIf). Used to resolve showIf visibility for card
   *  completion (s111, D-110.3). Typed loosely so the real `GenericField` satisfies this
   *  structurally (its `conditions` uses the operator UNION); cast at the evaluate call. */
  conditions?: unknown;
}

export interface CompletionSection {
  slug: string;
  layoutType?: string; // 'card' | 'table' | 'expandable'
  fields: CompletionField[];
  /** 🔴 s156 (E-fix): does the spec declare an EMPTY STARTER row? REQUIRED — it decides whether
   *  an all-blank seeded row is a starter the planner SEES (score it) or a legacy artifact the
   *  render path hides (don't). The first cut took the raw `defaultRows` as OPTIONAL, and the
   *  dashboard — which builds its section object from the section-overview aggregate — didn't
   *  have them, so it scored a different row set than the category page and reported a section
   *  complete that the page showed unfinished. Now the aggregate carries the resolved boolean
   *  and every caller must pass it. */
  declaresEmptyStarter: boolean;
}

export interface CompletionEntry {
  fieldId: string;
  rowId?: string | null;
  value?: string | null;
  isSeeded?: boolean | null;
  /** s156 (D-156 ruling 8b): row-level soft-delete flag, so completion measures only rows the
   *  planner can see. Optional — a caller that omits it keeps the previous behaviour. */
  isHidden?: boolean | null;
  /** s98 per-field "I don't have this" — a declined entry counts as resolved. */
  declined?: boolean | null;
}

export type { SectionProgress, SectionProgressResult } from "@shared/section-progress";

/** s156 (E-fix) — the ONE way a caller holding raw spec `defaultRows` (the category page's
 *  section objects) turns them into the `CompletionSection` the math wants. The dashboard gets
 *  the same boolean pre-resolved on the section-overview aggregate, so both paths agree by
 *  construction rather than by both remembering to do the same thing. */
export function completionSectionOf<T extends { slug: string; layoutType?: string | null; fields: CompletionField[]; defaultRows?: unknown }>(
  section: T,
): CompletionSection {
  return {
    slug: section.slug,
    layoutType: section.layoutType ?? undefined,
    fields: section.fields,
    declaresEmptyStarter: sectionDeclaresEmptyStarter(
      section.defaultRows as Array<Record<string, string>> | null | undefined,
    ),
  };
}

/** Map the page's cache shape into the shared function's plain input. Resolves Cat A
 *  through the client fail-closed helper and normalises `isSeeded` to a real boolean
 *  (null → false = user-touched). Card sections collapse to ONE synthetic row. */
function toProgressInput(
  section: CompletionSection,
  allEntries: ReadonlyArray<CompletionEntry>,
  manualNa?: boolean,
  removedRowIds?: ReadonlySet<string>,
): SectionProgressInput {
  const fieldIds = new Set(section.fields.map((f) => f.id));
  const idToKey = new Map(section.fields.map((f) => [f.id, f.fieldKey]));
  // Phase 4b closure (s145): drop executor-removed rows (soft_delete) before scoring, so
  // a discovered row the executor later removed stops counting toward the section pill.
  // Uses the SAME marker the plan-view render overlay already receives (executorRemovedRowIds
  // → /api/clients/:id/executor-removed-rows). Card entries (rowId null) never match. Empty
  // set (the planner, always) → no-op. No second predicate, no second fetch (G7).
  const sectionEntries = allEntries.filter(
    (e) => fieldIds.has(e.fieldId) && !(e.rowId && removedRowIds?.has(e.rowId)),
  );

  const layoutType: SectionProgressInput["layoutType"] =
    section.layoutType === "card"
      ? "card"
      : section.layoutType === "expandable"
        ? "expandable"
        : "table";

  // showIf-aware card completion (s111, D-110.3): resolve each field's visibility from
  // the card's own entries so a required field hidden by its `showIf` is excluded from
  // total+filled. Card sections only — single-instance, so one entry per fieldId; list
  // showIf is per-row and stays with the row logic. Keyed by fieldId (what showIf targets).
  const isCard = layoutType === "card";
  const showIfEntryMap: ShowIfEntryMap = {};
  if (isCard) {
    for (const e of sectionEntries) showIfEntryMap[e.fieldId] = { value: e.value ?? "" };
  }

  const fields = section.fields.map((f) => ({
    fieldKey: f.fieldKey,
    isRequired: !!f.isRequired,
    isCategoryA: isCategoryAField(f),
    fieldType: f.fieldType ?? "",
    hiddenByShowIf: isCard ? !evaluateShowIf((f.conditions ?? null) as ShowIfCondition | null, showIfEntryMap) : undefined,
    // s155 (D-155.1): severityOptional is a spec-only flag (no DB col, absent from the API
    // field), resolved here from the spec allowlist — same client-side resolution as isCategoryA.
    severityOptional: isSeverityOptionalField(section.slug, f.fieldKey),
  }));

  // s156: RAW cells (visibility not yet decided) — isHidden rides along so the shared builder can
  // apply the render-path predicates itself.
  const toRaw = (e: CompletionEntry) => ({
    fieldId: e.fieldId,
    fieldKey: idToKey.get(e.fieldId) ?? "",
    value: e.value ?? null,
    isSeeded: e.isSeeded === true,
    isHidden: e.isHidden ?? null,
    declined: e.declined === true,
  });

  // s156 (F / D-156): rows are grouped here (only this side knows the fieldId-keyed cache shape);
  // EVERY downstream decision — per-row visibility, completion mode, the shaded set, the header field —
  // is made by the SHARED builder, so the server essentials adapter cannot reach a different answer.
  const rawRows: RawProgressRow[] =
    layoutType === "card"
      ? [{ rowId: "__card__", entries: sectionEntries.map(toRaw) }]
      : Array.from(
          sectionEntries.reduce((m, e) => {
            const rowId = e.rowId ?? "__norow__";
            m.set(rowId, [...(m.get(rowId) ?? []), e]);
            return m;
          }, new Map<string, CompletionEntry[]>()).entries(),
        ).map(([rowId, raw]) => ({ rowId, entries: raw.map(toRaw) }));

  return buildSectionProgressInput({
    sectionSlug: section.slug,
    layoutType,
    declaresEmptyStarter: section.declaresEmptyStarter,
    fields,
    rows: rawRows,
    manualNa,
    // s156 Part B: per-field gates, so the SHARED builder resolves list showIf per row (a charity
    // beneficiary row hides the person-only columns; the row beside it does not).
    gates: Object.fromEntries(
      section.fields.map((f) => [f.fieldKey, { fieldId: f.id, conditions: (f.conditions ?? null) as ShowIfCondition | null }]),
    ),
  });
}


/**
 * Full per-section progress: `{ complete, filled, total }`, or `null` when there is
 * nothing to measure (hidden section, or no required field). One shared computation.
 */
export function sectionProgress(
  section: CompletionSection,
  allEntries: ReadonlyArray<CompletionEntry>,
  opts?: { manualNa?: boolean; removedRowIds?: ReadonlySet<string> },
): SectionProgressResult {
  return computeSectionProgress(toProgressInput(section, allEntries, opts?.manualNa, opts?.removedRowIds));
}

/**
 * True iff `section` is complete given `allEntries`. Thin wrapper over the shared
 * `sectionProgress` — the single completion source (do not fork).
 */
export function isSectionComplete(
  section: CompletionSection,
  allEntries: ReadonlyArray<CompletionEntry>,
  opts?: { manualNa?: boolean; removedRowIds?: ReadonlySet<string> },
): boolean {
  return sectionProgress(section, allEntries, opts)?.complete ?? false;
}
